import styled from "@emotion/styled";
import { lighten, spacing, SpacingProps } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Typography,
  Divider as MuiDivider,
  Accordion,
  AccordionSummary,
  AccordionActions,
  AccordionDetails,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  Chip as MuiChip,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IDocument } from "../../../../types/document";
import { DialogMode } from "../../../../types/dialogmode";
import {
  ContractProgress,
  IContract,
  LoadingState,
} from "../../../../types/contract";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";

import { AutoAwesome, SmartButton, ThumbDown } from "@mui/icons-material";

import {
  useDeleteAIOutputMutation,
  useGetHumanDataVerificationsQuery,
  useUpdateContractMutation,
} from "../../../../redux/slices/indexApiSlice";
import { IPrompt } from "../../../../types/prompt";
import {
  ContractExtractionResult,
  Paragraph,
  PromptExtractionResult,
} from "../../../../types/aiOutput";
import AISuggestion from "./AISuggestion";
import VerifiedAISuggestion from "./VerifiedAISuggestion";
import { TreeItem2 } from "@mui/x-tree-view/TreeItem2";
import useContract from "../../../../hooks/useContract";
import useBotChat from "../../../../hooks/useBotChat";
import Markdown from "react-markdown";
import LogicalCheckAlert from "./LogicalCheckAlert";
import RelevantSectionsSpykio from "./RelevantSectionsSpykio";

const Divider = styled(MuiDivider)(spacing);

interface ChipProps extends SpacingProps {
  component?: React.ElementType;
  href?: string;
  icon?: JSX.Element | null;
}
const Chip = styled(MuiChip)<ChipProps>(spacing);

export const Content = ({
  currentResult,
  selectParagraph,
}: {
  currentResult: PromptExtractionResult;
  selectParagraph: (paragraph: Paragraph) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
      }}
    >
      <Markdown
        components={{
          p: ({ node, ...props }) => (
            <p
              style={{
                marginBlockStart: "0.5rem",
                marginBlockEnd: "0.5rem",
              }}
              {...props}
            />
          ),
          code: ({ node, ...props }) => (
            <code
              style={{
                fontFamily: "Inter",
                whiteSpace: "pre-wrap",
              }}
              {...props}
            />
          ),
        }}
      >
        {currentResult?.extractionResult.details}
      </Markdown>

      {/* 
      <Typography variant="caption" fontWeight={600}>
        {t("Sources", {
          count: currentResult?.relevantSections.length,
        })}
      </Typography> */}
      <RelevantSectionsSpykio spykioContext={currentResult.context} />
    </div>
  );
};

const ContractPromptAccordion = ({
  contract,
  mode,
  selectParagraph,
  prompt,
  isExpanded,
  aiOutputs,
  infoFound,
  documents,
}: {
  contract: IContract;
  mode: DialogMode;
  aiOutputs: ContractExtractionResult[];
  selectParagraph: (
    paragraph?: Paragraph,
    prompt?: IPrompt,
    aiOutput?: ContractExtractionResult
  ) => void;
  prompt: IPrompt;
  isExpanded: boolean;
  infoFound: boolean;
  documents: IDocument[];
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { contractProgresses, analyzingNumber, analyzingSuccess } =
    useContract();

  const [currentContractProgress, setCurrentContractProgress] =
    useState<ContractProgress>();

  const { data: verifications } = useGetHumanDataVerificationsQuery(contract);

  const [updateContract, { isLoading: isUpdatingContract }] =
    useUpdateContractMutation();

  const [deleteAIOutput] = useDeleteAIOutputMutation();

  const [aiOutputsForPrompt, setAiOutputsForPrompt] = useState<
    ContractExtractionResult[]
  >([]);

  const [latestAiOutput, setLatestAiOutput] =
    useState<ContractExtractionResult>();

  const [latestPromptResult, setLatestPromptResult] =
    useState<PromptExtractionResult>();

  const [isExcluded, setIsExcluded] = useState(false);

  const {
    open: isChatOpen,
    setOpen: setShowChat,
    setAddedMessage,
  } = useBotChat();

  useEffect(() => {
    if (contractProgresses && contractProgresses.length > 0) {
      const currentProgress = contractProgresses.find(
        (p) => p.contractId === contract.id
      );

      if (currentProgress) {
        setCurrentContractProgress(currentProgress);
      } else {
        setCurrentContractProgress(undefined);
      }
    }
  }, [contractProgresses]);

  useEffect(() => {
    if (contract?.exludedPromptIds) {
      if (prompt.id) {
        setIsExcluded(contract.exludedPromptIds.includes(prompt.id));
      }
    }
  }, [contract.exludedPromptIds]);

  useEffect(() => {
    if (aiOutputs && aiOutputs.length > 0) {
      const promptResults = aiOutputs.filter((a) =>
        a.prompts?.some((p) => p.promptID === prompt.id)
      );

      if (promptResults?.length > 0) {
        setLatestAiOutput(promptResults[0]);
      }
    }

    if (aiOutputs) {
      setAiOutputsForPrompt(
        aiOutputs.filter((a) =>
          a.prompts?.some((p) => p.promptID === prompt.id)
        )
      );
    }
  }, [aiOutputs]);

  useEffect(() => {
    if (latestAiOutput) {
      setLatestPromptResult(
        latestAiOutput.prompts?.find((p) => p.promptID === prompt.id)
      );
    }
  }, [latestAiOutput]);

  const verificationRecords = verifications?.filter(
    (v) => v.field === prompt.id
  );

  const latestVerificationRecord = verificationRecords?.find(
    (v) => v.aiOutputId === latestAiOutput?.id
  );

  const latestResultIsVerified =
    latestVerificationRecord?.isCorrect !== undefined;

  const exclude = () => {
    if (!prompt.id) {
      return;
    }

    setIsExcluded(true);
    updateContract({
      ...contract,
      exludedPromptIds: [...(contract.exludedPromptIds || []), prompt.id],
    });
  };

  const include = () => {
    if (!prompt.id) {
      return;
    }

    setIsExcluded(false);
    updateContract({
      ...contract,
      exludedPromptIds:
        contract.exludedPromptIds?.filter((id) => id !== prompt.id) || [],
    });
  };

  const handleExclusion = () => {
    if (isExcluded) {
      include();
    } else {
      exclude();
    }
  };

  const isAnalyzing =
    currentContractProgress?.loadingStatePerPrompt &&
    prompt.id &&
    currentContractProgress?.loadingStatePerPrompt[prompt.id] ===
      LoadingState.DATA_EXTRACTION;

  const launchChat = (question: string) => {
    setShowChat(true);

    // console.log("Enter key pressed");
    // postQuestion({ question: event.target.value });
    setAddedMessage(question);
  };

  const result = contract.prompts?.find((p) => p.promptID === prompt.id);
  const riskFound = result?.logicalCheck?.logic_check?.is_logical === false;

  return (
    <Accordion
      key={`accordion-${prompt.id}`}
      onChange={(e, expanded) =>
        selectParagraph(
          undefined,
          expanded ? prompt : undefined
          // latestAiOutput
        )
      }
      expanded={isExpanded}
      id={`panel-${prompt.id}-block`}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel-${prompt.id}`}
        id={`panel-${prompt.id}-header`}
      >
        <ListItem
          disablePadding
          disableGutters
          secondaryAction={
            riskFound && (
              <Chip
                label={t("Risk found")} // need to classify low, medium, high
                color="warning"
                size="small"
                sx={{
                  textTransform: "uppercase",
                  // use warning.light but make it lighter
                  backgroundColor: lighten(theme.palette.warning.light, 0.8),
                  color: "warning.main",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: lighten(theme.palette.warning.light, 0.7),
                    color: "warning.main",
                  },
                }}
                variant="filled"
                onClick={() => console.log("Risk chip clicked")}
              />
            )
          }
        >
          <ListItemIcon
            sx={{
              color: contract.prompts?.some((p) => p.promptID === prompt.id)
                ? theme.palette.primary.main
                : theme.palette.secondary.main,
            }}
          >
            {isAnalyzing ? <CircularProgress size={16} /> : <SmartButton />}
          </ListItemIcon>
          <ListItemText
            primary={
              <Grid container alignItems="center" gap={1}>
                {prompt?.title ? t(prompt.title) : t("Unknown")}
                {latestVerificationRecord?.isCorrect === false && (
                  <ThumbDown fontSize="small" color="error" />
                )}
              </Grid>
            }
            secondary={
              contract.prompts?.find((p) => p.promptID === prompt.id)
                ? contract.prompts
                    ?.find((p) => p.promptID === prompt.id)
                    ?.extractionResult?.details?.substring(0, 100) + "..." || ""
                : undefined
            }
            slotProps={{
              secondary: {
                sx: {
                  paddingRight: riskFound ? 22 : 0, // if the risk chip is present, add padding to the right
                },
              },
            }}
          />
        </ListItem>
      </AccordionSummary>
      <AccordionDetails>
        {result?.logicalCheck && (
          <LogicalCheckAlert logicalCheck={result.logicalCheck} />
        )}

        {result && (
          <Content currentResult={result} selectParagraph={selectParagraph} />
        )}

        <Box gap={2} display="flex" flexDirection="row">
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<AutoAwesome />}
            onClick={() =>
              launchChat(
                t("contract_prompts.chat_question", {
                  promptTitle: prompt.title || t("Unknown"),
                })
              )
            }
            sx={{
              borderColor: "grey.500",
            }}
          >
            {t("Tell me more")}
          </Button>

          <Tooltip
            title={
              isExcluded
                ? t("Include prompt in contract description")
                : t("Exclude prompt from contract description")
            }
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleExclusion}
              disabled={isUpdatingContract}
              sx={{
                borderColor: "grey.500",
              }}
            >
              {isExcluded
                ? t("Include prompt in contract")
                : t("Exclude prompt from contract")}
            </Button>
          </Tooltip>
        </Box>
        {aiOutputsForPrompt?.length > 0 && latestAiOutput && (
          <Box my={5}>
            <SimpleTreeView
              onExpandedItemsChange={(e, itemIds) => {
                console.log(e);

                if (itemIds?.length > 0) {
                  const expandedAiOutput = aiOutputsForPrompt.find(
                    (a) => a.id === itemIds[0]
                  );
                  selectParagraph(undefined, prompt, expandedAiOutput);
                }
              }}
            >
              {aiOutputsForPrompt?.map((aiOutput, i) => {
                // if (aiOutput.id === latestAiOutput?.id) {
                //   // skip the latest ai output as it is already shown
                //   return null;
                // }

                const currentResult = aiOutput.prompts?.find(
                  (p) => p.promptID === prompt.id
                );

                if (!currentResult) {
                  return null;
                }

                const verificationRecord = verifications?.find(
                  (v) => v.field === prompt.id && v.aiOutputId === aiOutput.id
                );

                const document = documents.find(
                  (d) => d.id === aiOutput.documentId
                );

                return (
                  <TreeItem2
                    itemId={`${aiOutput.id}-${i}`}
                    label={
                      <Grid container justifyContent="space-between">
                        <Typography>
                          {aiOutput.processedAt
                            ? t("date.dateTime", {
                                date: new Date(aiOutput.processedAt),
                              })
                            : "No processing date"}
                        </Typography>
                        {/* {documents.length > 1 && (
                            <DocumentChip document={document} />
                          )} */}
                        {/* <Button onClick={() => deleteAIOutput(aiOutput)}>
                            Delete
                          </Button> */}
                      </Grid>
                    }
                    style={{
                      marginBottom: 5,
                      gap: 5,
                    }}
                    slotProps={{
                      content: {
                        style: {
                          paddingTop: 10,
                          paddingBottom: 10,
                        },
                      },
                    }}
                  >
                    <Box my={2} display="flex" flexDirection="column" gap={2}>
                      {document && documents?.length > 1 && (
                        <div>
                          <Chip
                            label={
                              document.type === "attachment"
                                ? document.name
                                : t("Main document")
                            }
                            color="primary"
                            size="small"
                            // icon={<AttachFile />}
                            variant="outlined"
                          />
                        </div>
                      )}

                      {verificationRecord && (
                        <VerifiedAISuggestion
                          verificationRecord={verificationRecord}
                        />
                      )}

                      <AISuggestion
                        contract={contract}
                        mode={mode}
                        isAiSuggestion={!verificationRecord}
                        collapsible={false}
                        isVerified={verificationRecord?.isCorrect !== undefined}
                        field="prompts"
                        prompt={prompt}
                        value={currentResult}
                        aiInputId={aiOutput.aiInputId}
                        aiOutputId={aiOutput.id}
                        content={
                          <Content
                            currentResult={currentResult}
                            selectParagraph={(paragraph) => {
                              selectParagraph(paragraph, prompt, aiOutput);
                            }}
                          />
                        }
                        processedAt={aiOutput?.processedAt}
                      />
                    </Box>
                  </TreeItem2>
                );
              })}
            </SimpleTreeView>
          </Box>
        )}
      </AccordionDetails>
      {mode === DialogMode.Edit && <AccordionActions></AccordionActions>}
    </Accordion>
  );
};

export default ContractPromptAccordion;
